import axios from 'axios'
import { get } from './base'
import { useQuery } from '@tanstack/vue-query'
import { computed, isRef, unref } from 'vue'
import { MaybeRef } from '@vueuse/core'
import moment from 'moment'
import { VersionedSchema } from '@/models/versioned-schema'
import { CustomFieldsSchemaObject, CustomFieldsUiSchema } from '@/models/types/custom-fields-schema'
import { convertTo } from '@/models/utils/casing'

const v3Endpoint = '/api/v3/schemas'
const v4Endpoint = '/api/v4/schemas'

export interface SyncTargetSchema {
  syncTargetId: string
  name: string
  type: string
  schema: { schema?: CustomFieldsSchemaObject; uiSchema?: CustomFieldsUiSchema }
}

export function getAllSyncTargetSchemas() {
  return get<SyncTargetSchema[]>(`${v4Endpoint}/invoice-sync-targets`).then((res) =>
    convertTo<SyncTargetSchema[]>(res.data, [{ key: 'schema' }]),
  )
}

export function useAllSyncTargetSchemasQuery() {
  return useQuery({
    queryKey: ['schemas', 'invoice-sync-targets'],
    queryFn: () => getAllSyncTargetSchemas(),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
  })
}

export function getJobCustomFieldsSchema() {
  const source = axios.CancelToken.source()

  return get(`${v3Endpoint}/job-custom-data`, { cancelToken: source.token })
}

export function getExtensionSchema(extensionSchemaId) {
  const source = axios.CancelToken.source()

  return get(`${v3Endpoint}/activities/versions/${extensionSchemaId}`, {
    cancelToken: source.token,
  })
}

export async function getExtensionSchemaFormatted(extensionSchemaId) {
  // Some schema use older structure (i.e., no "schema", "uiSchema"
  // keys) so we handle both cases below.
  const response = await getExtensionSchema(extensionSchemaId)
  if (!response.data?.schema) {
    return { schema: response.data }
  } else {
    return response.data
  }
}

export function getExtensionDataSchema(divisionId, activityId) {
  return get(`${v3Endpoint}/divisions/${divisionId}/activities/${activityId}`)
}

export function useExtensionDataQuery(divisionId, activityId) {
  const enabled = computed(
    () => !!((!isRef(divisionId) || divisionId.value) && (!isRef(activityId) || activityId.value)),
  )
  const { data } = useQuery({
    queryKey: ['schemas', activityId, divisionId],
    queryFn: () => getExtensionDataSchema(unref(divisionId), unref(activityId)).then((r) => r.data),
    enabled,
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
  })

  return {
    data,
  }
}

export function getSyncTargetCustomSchema(appId, schemaId) {
  const source = axios.CancelToken.source()

  return get<VersionedSchema>(`${v4Endpoint}/invoice-sync-target/${appId}/versions/${schemaId}`, {
    cancelToken: source.token,
  })
}

export function useSyncTargetCustomSchema(options: {
  appId?: MaybeRef<string | null | undefined>
  schemaId?: MaybeRef<number | null | undefined>
}) {
  const appId = computed(() => unref(options.appId))
  const schemaId = computed(() => unref(options.schemaId))

  const enabled = computed(() => {
    return appId.value != null && schemaId.value != null
  })
  const queryKey = computed(() => ['schemas', appId.value, schemaId.value])

  return useQuery({
    queryKey: queryKey.value,
    queryFn: () => getSyncTargetCustomSchema(unref(appId), unref(schemaId)).then((r) => r.data),
    enabled,
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
  })
}
